import { getCookie } from './cookie_reader';

// The cookies OneTrust writes. OptanonConsent carries the visitor's per-group choices; OptanonAlertBoxClosed
// appears once they have answered the consent banner (so its absence on a first visit means a banner is due).
export const ONETRUST_CONSENT_COOKIE = 'OptanonConsent';
export const ONETRUST_BANNER_CLOSED_COOKIE = 'OptanonAlertBoxClosed';

// OneTrust cookie categories → the group ids OneTrust reports as active. OneTrust is the source of truth: the ids
// are the "default values as set by OneTrust" (Strictly Necessary C0001, Performance C0002, Functional C0003,
// Targeting C0004, Social Media C0005) in https://developer.onetrust.com/onetrust/docs/when-consent-changes, and the
// keys are its category names in kebab case (`social` is the shorthand for Social Media). Which category a given
// vendor or cookie sits in is OneTrust console configuration, not something these ids say.
// Not to be confused with `./consent.js`, which is TCF v2 / IAB vendor consent.
export const CONSENT_CATEGORY_GROUPS = {
  'strictly-necessary': 'C0001',
  performance: 'C0002',
  functional: 'C0003',
  targeting: 'C0004',
  social: 'C0005',
};

// Consent-mode signal names, keyed to the same group ids as CONSENT_CATEGORY_GROUPS.
// The OneTrust plugin translates a granted group into ad_storage / analytics_storage from this map.
export const ANALYTICS_STORAGE_GROUP_ID = CONSENT_CATEGORY_GROUPS.performance;
export const ADVERTISING_GROUP_ID = CONSENT_CATEGORY_GROUPS.targeting;

export const COOKIE_GROUP_ID_MAP = {
  ad_storage: ADVERTISING_GROUP_ID,
  ad_user_data: ADVERTISING_GROUP_ID,
  ad_personalization: ADVERTISING_GROUP_ID,
  analytics_storage: ANALYTICS_STORAGE_GROUP_ID,
};

/**
 * Whether OneTrust is in use for this site. On by default; a site switches it off with
 * `oneTrust: { enabled: false }` in its settings. One definition, so every consumer — the consent plugin,
 * the consent-gated cookie write, the interruptive's banner check — agrees on what "off" means.
 *
 * @param  {Object} settings  `app.settings`
 * @return {boolean}
 */
export function isOneTrustEnabled(settings) {
  return settings?.oneTrust?.enabled !== false;
}

/**
 * The group id one entry of an active-groups list grants, or null if it grants none. An entry takes either
 * form, and both are read the same way wherever the list came from:
 *
 *   - `C0003`    — the id alone, as in `window.OnetrustActiveGroups` (`",C0001,C0003,"`) and in the array of
 *                  OneTrust's change event. The primary form. Present means active.
 *   - `C0003:1`  — the id and a status. A FALLBACK, for the `OptanonConsent` cookie's `groups=` value
 *                  (`C0001:1,C0002:0,…`), which newer versions of the OneTrust SDK write in this form; the SDK on a
 *                  given page may be older and not. Only a status of `1` is consent; `0`, `-1` (not collected) or
 *                  anything else is not, so an unknown status fails closed. OneTrust's statuses are 1 = consent
 *                  given, 0 = not given, -1 = not collected
 *                  (https://developer.onetrust.com/onetrust/docs/when-consent-changes); that page does not
 *                  document the cookie's `groups=` format itself.
 *
 * An entry in any other form is read as an id of its own, which matches no category, so it grants nothing.
 *
 * Blank entries (the commas around `",C0001,"` leave them) grant nothing.
 *
 * @param  {string} entry
 * @return {string|null}
 */
export function groupFromEntry(entry) {
  const text = String(entry).trim();
  const separator = text.indexOf(':');

  if (separator === -1) return text || null;

  const id = text.slice(0, separator).trim();
  const status = text.slice(separator + 1).trim();

  return id && status === '1' ? id : null;
}

/**
 * Normalise a list of active groups to an array of the group ids it grants (see {@link groupFromEntry} for the
 * two entry forms). The list is `window.OnetrustActiveGroups` (a comma-delimited string with a leading and
 * trailing comma, `",C0001,C0003,"`), the `groups=` value of the `OptanonConsent` cookie, or an array such as
 * the one in OneTrust's change event; all come out the same here.
 *
 * @param  {string|string[]} value
 * @return {string[]}
 */
export function parseActiveGroups(value) {
  if (!value) return [];
  // Only the two forms OneTrust actually reports. Anything else fails closed
  // instead of being stringified into a group id.
  if (typeof value !== 'string' && !Array.isArray(value)) return [];

  const entries = Array.isArray(value) ? value : value.split(',');
  return entries.map(groupFromEntry).filter(Boolean);
}

/**
 * The OneTrust group ids the visitor has allowed, or null when OneTrust hasn't reported any.
 *
 * `window.OnetrustActiveGroups` is the source, and is authoritative when OneTrust has set it. Only when it is not
 * set does this fall back to the `OptanonConsent` cookie, whose `groups=` value newer SDK versions write as
 * `C0001:1,C0002:0,…`. Both are read by {@link parseActiveGroups}, so `C0003` and `C0003:1` each grant the group and
 * `C0003:0` does not. If the SDK on the page writes that cookie in a different form, the fallback finds nothing
 * and the answer is no: the failure is in the safe direction.
 *
 * @return {string[]|null}
 */
export function getActiveGroups() {
  try {
    const active = window.OnetrustActiveGroups;
    if (active) return parseActiveGroups(active);

    // The OptanonConsent value is `isGpcEnabled=0&datestamp=…&groups=…`, so it contains '=' itself; getCookie()
    // splits at the first '=' only, so it comes back whole. (Imported from cookie_reader.js, which imports nothing:
    // cookie_writer.js imports THIS module, so reading through it would be a cycle.)
    const consent = getCookie(ONETRUST_CONSENT_COOKIE);
    if (!consent) return null;

    const groups = decodeURIComponent(consent).match(/groups=([^&]+)/);
    if (!groups) return null;

    return parseActiveGroups(groups[1]);
  } catch (error) {
    return null;
  }
}

/**
 * Whether a list of active group ids allows a OneTrust cookie category. Pure: the list is passed in, so a
 * caller that already has one — such as the group array in the `OneTrustGroupsUpdated` event — checks it
 * against the same category map as everything else instead of repeating group ids.
 *
 * Fails closed: an unknown category, or anything that is not an array, is no.
 *
 * @param  {string[]} groups
 * @param  {'strictly-necessary'|'performance'|'functional'|'targeting'|'social'} category
 * @return {boolean}
 */
export function groupsAllow(groups, category) {
  const group = CONSENT_CATEGORY_GROUPS[category];
  return Boolean(group) && Array.isArray(groups) && groups.includes(group);
}

/**
 * Whether a list of active groups grants Performance (C0002) — the OneTrust equivalent of Google
 * consent mode's `analytics_storage`, and the gate for persisting the session pageview count
 * (ADS-65). Pure: pass the list in (`window.OnetrustActiveGroups` or the change-event array), so it
 * is parsed and checked against the same {@link CONSENT_CATEGORY_GROUPS} map as every other
 * consumer rather than repeating the `C0002` id.
 *
 * @param  {string|string[]} activeGroups
 * @return {boolean}
 */
export function activeGroupsAllowPerformance(activeGroups) {
  return groupsAllow(parseActiveGroups(activeGroups), 'performance');
}

/**
 * Whether OneTrust's active groups grant analytics storage (C0002). Same check as
 * {@link activeGroupsAllowPerformance}. Kept so callers and tests that use this name
 * agree with the Performance (C0002) category gate.
 *
 * @param  {string|string[]} activeGroups
 * @return {boolean}
 */
export function activeGroupsGrantAnalytics(activeGroups) {
  return activeGroupsAllowPerformance(activeGroups);
}

/**
 * Whether the visitor has consented to a OneTrust cookie category, as OneTrust reports it right now.
 *
 * Fails closed: an unknown category, a missing or empty group list, or anything that throws all mean
 * no. There is deliberately no strictly-necessary bypass — `strictly-necessary` (C0001) must itself
 * appear in the active groups, so until OneTrust has reported any groups, nothing is allowed.
 *
 * @param {'strictly-necessary'|'performance'|'functional'|'targeting'|'social'} category
 * @return {boolean}
 */
export function hasConsentFor(category) {
  return groupsAllow(getActiveGroups(), category);
}
